import { applicationContext, uuid, QueryUtility, toUTCDate } from 'core/utils';
import { BaseViewStore, BaasicDropdownStore } from 'core/stores';
import { action, observable, computed, runInAction, set } from 'mobx';
import { FacilityOrderService, FacilityOrderStatusService, ItemSubcategoryService } from "application/facility-order/services";
import { FacilityService } from "application/facility/services";
import { LookupService } from "common/services";
import { ItemCategoryService } from 'application/product/services';
import _ from 'lodash';
import { NikoPatientDetailService, NikoPatientTemplateService, NikoPatientTemplateItemService } from "application/niko-patient/services";
import { ModalParams } from 'core/models';
import { HistoryService } from '../../history/services';

@applicationContext
class NikoFacilityOrderInitialIntakePreviewViewStore extends BaseViewStore {
    @observable accessor facilityOrder;
    @observable accessor refreshKey = uuid();
    @observable accessor selectedStatusAbrv;
    @observable accessor itemCategories = [];
    @observable accessor fetchingData = false;
    @observable accessor disabled = false;
    @observable accessor nikoPatientDetails = [];
    @observable accessor isReviewed = undefined;

    @computed
    get initialIntakeStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "initial-intake") : undefined; }
    @computed
    get insuranceReviewStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "insurance-review") : undefined; }
    @computed
    get usIntakeStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "us-intake") : undefined; }
    @computed
    get disabledByUsIntake() { return this.usIntakeStatus && !(this.initialIntakeStatus && this.initialIntakeStatus.completedOn); }
    @computed
    get disabledByStatus() { return !(this.initialIntakeStatus && this.initialIntakeStatus.startedOn); }
    @computed
    get disabledByCompletion() { return !!(this.initialIntakeStatus && this.initialIntakeStatus.completedOn) || !!(this.insuranceReviewStatus && this.insuranceReviewStatus.startedOn) || !!(this.usIntakeStatus && this.usIntakeStatus.startedOn); }
    @computed
    get disabledBySendOrder() { return !this.isAdmin && this.facilityOrder && this.facilityOrder.sendOrder; }

    tabItems = [{ abrv: 'in-progress', name: 'In Progress' }, { abrv: 'discharge', name: 'Discharges' }, { abrv: 'intake-reviewed', name: 'Intake Reviewed' }];
    statuses = [{ id: "Hospital" }, { id: "Discharged" }, { id: "Expired" }, { id: "Active" }];

    constructor(rootStore) {
        super(rootStore);

        this.id = rootStore.routerStore.routerState.params.id;
        this.isAdmin = this.rootStore.userStore.user.roles.includes("Administrators");
        this.pageName = 'ii';

        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.itemCategoryService = new ItemCategoryService(rootStore.application.baasic.apiClient);
        this.nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        this.patientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        this.patientTemplateItemService = new NikoPatientTemplateItemService(rootStore.application.baasic.apiClient);
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.itemSubcategoryService = new ItemSubcategoryService(rootStore.application.baasic.apiClient);
        this.historyService = new HistoryService(rootStore.application.baasic.apiClient);

        const userRoles = this.rootStore.userStore.applicationUser.roles;
        this.isHistoryVisible = !(userRoles.includes("InitialIntake") || userRoles.includes("InsuranceReview"));

        this.itemCategoryStore = new BaasicDropdownStore(
            { multi: true, filterable: true, initFetch: false },
            {
                fetchFunc: async (term) => {
                    const response = await this.itemCategoryService.find({ searchQuery: term, rpp: 100 });
                    return response.data.item.filter(i => this.facilityOrder.isEnteralWeek ? i.abrv === "enteral" : (i.abrv === "enteral" || i.abrv === "respiratory" || i.abrv === "urological" || i.abrv === "ostomy"));
                },
            }
        );

        this.statusStore = new BaasicDropdownStore(
            { multi: false, filterable: false, textField: "id" },
            { fetchFunc: () => this.statuses }
        );

        this.nikoTemplateCreateModal = new ModalParams({});
        this.patientTemplateDiscontinuedModal = new ModalParams({});
        this.nikoPatientStatusModal = new ModalParams({});
        this.patientTemplateDeleteModal = new ModalParams({});
        this.nikoPatientTemplateItemCalculationModal = new ModalParams({});
        this.nikoPatientTemplateItemNewFlowDeleteQuestionModal = new ModalParams({});
        this.patientTemplateNoteModal = new ModalParams({});
        this.nikoTemplateMissingProductNoteModal = new ModalParams({});
        this.nikoFacilityOrderNewFlowExtraSuppliesAddModal = new ModalParams({});
        this.nikoCreateNewPatientModal = new ModalParams({});

        this.templateChangeConfirmModal = new ModalParams({ onClose: null });
        this.templateChangeConfirmModal.onConfirm = async () => { this.templateChangeConfirmModal.close(); };
        this.templateChangeConfirmModal.yesLabel = 'MODAL.ACKNOWLEDGE';
        this.templateChangeConfirmModal.doNotShowCancel = true;

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
            await this.fetchFacilityOrderData(filter);
        }, {});

        this.fetch([
            this.initialize()
        ]);
    }

    async initialize() {
        await this.getFacilityOrder();
        await this.getLookups();
        await this.getData();
    }

    @action.bound
    async getFacilityOrder() {
        this.facilityOrder = (await this.facilityOrderService.get(this.id, { embed: 'Facility,FacilityOrderStatuses' })).data;
        this.rootStore.titleStore.setTitle(`Initial Intake - ${this.facilityOrder.facility.name}`);
    }

    @action.bound
    async getLookups() {
        this.lookups = await this.lookupService.getLookups("FacilityScheduleType,NewFlowStatus,ImportHeaderStatus,ImportAction,ItemCategory,SubCategory,SpecialtyType,DefaultItemType");
        this.orderingTypeId = this.lookups.facilityScheduleType.find((s) => s.abrv === 'ordering').id;
        this.inProgressStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'in-progress').id;
        this.changeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'change').id;
        this.noChangeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'no-change').id;
        this.dischargeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'discharge').id;
        this.activeNewFlowStatusIds = `${this.inProgressStatusId},${this.changeStatusId},${this.noChangeStatusId}`;

        this.insertImportActionId = this.lookups.importAction.find((s) => s.abrv === 'insert').id;
        this.deleteImportActionId = this.lookups.importAction.find((s) => s.abrv === 'delete').id;
        this.updateImportActionId = this.lookups.importAction.find((s) => s.abrv === 'update').id;

        this.ostomyItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'ostomy').id;
        this.enteralItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'enteral').id;
        this.respiratorySubCategoryId = this.lookups.subCategory.find((s) => s.abrv === 'Respiratory---trachs-supply').id;
        this.trachTubeSubCategoryId = this.lookups.subCategory.find((s) => s.abrv === 'Trach-Tube').id;
        this.syringeDefaultItemTypeId = this.lookups.defaultItemType.find((s) => s.abrv === 'syringe').id;

        const { EligibilityStatusService } = await import('application/eligibility-status/services');
        const eligibilityStatusService = new EligibilityStatusService(this.rootStore.application.baasic.apiClient);
        this.eligibilityStatuses = (await eligibilityStatusService.find({ rpp: 100 })).data.item;
    }

    @action.bound
    async getData() {
        this.page = "initial-intake";
        this.isInventoryStepAllowed = (await this.facilityOrderService.isNikoInventoryStepAllowed(this.id)).data;
        await this.itemCategoryStore.filterAsync();

        let queryParams = this.rootStore.routerStore.routerState.queryParams;
        const selectedStatus = _.find(this.lookups.newFlowStatus, s => {
            return queryParams && queryParams.intakeStatusIds && s.id === queryParams.intakeStatusIds;
        });
        const selectedStatusAbrv = selectedStatus ? selectedStatus.abrv : (this.initialIntakeStatus && this.initialIntakeStatus.completedOn && !(await this.hasAnyTemplateInProgress())) ? 'intake-reviewed' : 'in-progress';
        this.setStatus(selectedStatusAbrv);
    }

    @action.bound
    async hasAnyTemplateInProgress() {
        let params = {
            facilityOrderId: this.id,
            intakeStatusIds: this.activeNewFlowStatusIds,
            isInitialIntakeReviewed: false,
            pageSize: 1,
            fields: 'id',
        }
        return (await this.patientTemplateService.find(params)).data.item.some(x => x.id);
    }

    @action.bound
    setStatus(statusAbrv) {
        if (this.selectedStatusAbrv == statusAbrv || this.fetchingData) {
            return;
        }
        this.selectedStatusAbrv = statusAbrv;
        this.queryUtility.filter.set('intakeStatusIds', this.selectedStatusAbrv === "discharge" ? this.dischargeStatusId : this.activeNewFlowStatusIds);
        this.queryUtility.fetch();
    }

    @action.bound
    async fetchFacilityOrderData(filter) {
        this.fetchingData = true;
        var itemCategoryIds = _.map(this.itemCategories, (i => { return i.id }));
        let params = {
            ...filter,
            facilityOrderId: this.id,
            isInitialIntakeReviewed: this.selectedStatusAbrv == "in-progress" ? false : this.selectedStatusAbrv == "intake-reviewed" ? true : undefined,
            itemCategoryIds: (itemCategoryIds && itemCategoryIds.length > 0) ? itemCategoryIds : undefined,
            pageSize: 10000,
            embed: 'NikoPatientDetail,PatientTemplateItems,SubCategory,NikoPatientDetail.EligibilityStatus,NikoPatientDetail.NikoPatientDetailInsurances,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus,ItemCategory',
            orderBy: "SubCategory",
            orderDirection: "asc",
        }

        let responseData = (await this.patientTemplateService.find(params)).data.item;
        const groupedByPatientDetail = _.groupBy(responseData, t => t.nikoPatientDetail.id);
        let nikoPatientDetails = Object.values(groupedByPatientDetail).map(patientTemplates => ({
            ...patientTemplates[0].nikoPatientDetail,
            nikoPatient: { ...patientTemplates[0].nikoPatientDetail.nikoPatient, dateOfBirth: toUTCDate(patientTemplates[0].nikoPatientDetail.nikoPatient.dateOfBirth) },
            disabled: this.selectedStatusAbrv == "in-progress" && patientTemplates[0].nikoPatientDetail.status === 'Hospital',
            patientTemplates: patientTemplates.map(t => ({
                ...t,
                nikoPatientDetail: undefined,
                disabled: false,
                itemsVisible: false
            }))
        }));

        nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
            nikoPatientDetail.history = { userName: '', eventDate: null, fieldName: '' };
            this.setHasHistory(nikoPatientDetail);
            this.getPatientStatusButtonClassName(nikoPatientDetail);
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.tempNote = template.note;
                template.isAddingNote = false;
                template.patientTemplateItems && template.patientTemplateItems.forEach(item => {
                    item.savedQuantity = item.quantity;
                    item.savedSalesOrderQuantity = item.salesOrderQuantity;
                    item.savedNote = item.note;
                    item.history = item.history || { userName: '', eventDate: null, fieldName: '', action: '' };
                });
            });
        });

        if (this.initialIntakeStatus && !this.initialIntakeStatus.completedOn && this.selectedStatusAbrv != "intake-reviewed") {
            this.checkIsReviewed(false);
        }

        this.nikoPatientDetails = nikoPatientDetails;

        if (this.itemCategories.some(x => x.id) && this.nikoPatientDetails.length === 0) {
            this.rootStore.notificationStore.warning("No templates found under selected filter");
        }
        this.fetchingData = false;
    }

    @action.bound
    async fetchPatientTemplateByPatient(nikoPatientDetailId) {
        if (this.initialIntakeStatus && !this.initialIntakeStatus.completedOn && this.selectedStatusAbrv != "intake-reviewed") {
            await this.checkIsReviewed(false);
        }

        let index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;

        const itemCategoryIds = _.map(this.itemCategories, (i => { return i.id }));
        let params = {
            intakeStatusIds: this.queryUtility.filter.intakeStatusIds,
            facilityOrderId: this.id,
            isInitialIntakeReviewed: this.selectedStatusAbrv == "in-progress" ? false : this.selectedStatusAbrv == "intake-reviewed" ? true : undefined,
            itemCategoryIds: (itemCategoryIds && itemCategoryIds.length > 0) ? itemCategoryIds : undefined,
            pageSize: 10000,
            embed: 'NikoPatientDetail,PatientTemplateItems,SubCategory,NikoPatientDetail.EligibilityStatus,NikoPatientDetail.NikoPatientDetailInsurances,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus,ItemCategory',
            orderBy: "SubCategory",
            orderDirection: "asc",
            nikoPatientDetailId: nikoPatientDetailId
        }

        let responseData = (await this.patientTemplateService.find(params)).data.item;
        const groupedByPatientDetail = _.groupBy(responseData, t => t.nikoPatientDetail.id);
        let nikoPatientDetails = Object.values(groupedByPatientDetail).map(patientTemplates => ({
            ...patientTemplates[0].nikoPatientDetail,
            nikoPatient: { ...patientTemplates[0].nikoPatientDetail.nikoPatient, dateOfBirth: toUTCDate(patientTemplates[0].nikoPatientDetail.nikoPatient.dateOfBirth) },
            disabled: false,
            patientTemplates: patientTemplates.map(t => ({
                ...t,
                nikoPatientDetail: undefined,
                disabled: false,
                itemsVisible: false
            }))
        }));

        nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
            this.setHasHistory(nikoPatientDetail);
            this.getPatientStatusButtonClassName(nikoPatientDetail);
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.tempNote = template.note;
                template.isAddingNote = false;
                template.patientTemplateItems && template.patientTemplateItems.forEach(item => {
                    item.savedQuantity = item.quantity;
                    item.savedSalesOrderQuantity = item.salesOrderQuantity;
                    item.savedNote = item.note;
                    item.history = item.history || { userName: '', eventDate: null, fieldName: '', action: '' };
                });
            });
        });

        index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;

        if (nikoPatientDetails && nikoPatientDetails[0]) {
            runInAction(() => {
                Object.assign(this.nikoPatientDetails[index], nikoPatientDetails[0]);
            });
        }
        else {
            this.nikoPatientDetails.splice(index, 1);
        }

        this.shouldAutoNavigateToDischargesTab();
    }

    @action.bound
    async fetchPatientTemplateIntakeStatusById(patientTemplate) {
        if (this.selectedStatusAbrv == "discharge" || !patientTemplate) {
            return;
        }
        let responseData = (await this.patientTemplateService.get(patientTemplate.id, { fileds: 'IntakeStatusId' })).data;
        if (patientTemplate.intakeStatusId != responseData.intakeStatusId) {
            patientTemplate.intakeStatusId = responseData.intakeStatusId;
        }
    }

    @action.bound
    async fetchPatientTemplateItemsById(nikoPatientDetailId, patientTemplateId) {
        const index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;
        var patientTemplate = this.nikoPatientDetails[index].patientTemplates && this.nikoPatientDetails[index].patientTemplates.find(x => x.id == patientTemplateId);
        if (patientTemplate && patientTemplate.patientTemplateItems) {
            let responseData = (await this.patientTemplateService.get(patientTemplateId, { embed: 'PatientTemplateItems' })).data;
            if (responseData.patientTemplateItems) {
                runInAction(() => {
                    Object.assign(patientTemplate.patientTemplateItems, responseData.patientTemplateItems);
                    patientTemplate.patientTemplateItems.forEach(item => {
                        item.savedQuantity = item.quantity;
                        item.savedSalesOrderQuantity = item.salesOrderQuantity;
                        item.savedNote = item.note;
                    });
                });
            }
        }
    }

    @action.bound
    setHasHistory(nikoPatientDetail) {
        this.createInsuranceCompareModel(nikoPatientDetail);
        nikoPatientDetail.hasHistory = !nikoPatientDetail.isNew &&
            (
                (nikoPatientDetail.primaryActive.insuranceId.current != nikoPatientDetail.primaryActive.insuranceId.initial || nikoPatientDetail.primaryActive.policyNo.current != nikoPatientDetail.primaryActive.policyNo.initial) ||
                (nikoPatientDetail.secondaryActive.insuranceId.current != nikoPatientDetail.secondaryActive.insuranceId.initial || nikoPatientDetail.secondaryActive.policyNo.current != nikoPatientDetail.secondaryActive.policyNo.initial) ||
                (nikoPatientDetail.primaryInactive.insuranceId.current && (nikoPatientDetail.primaryInactive.insuranceId.current != nikoPatientDetail.primaryInactive.insuranceId.initial || nikoPatientDetail.primaryInactive.policyNo.current != nikoPatientDetail.primaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.secondaryInactive.insuranceId.current && (nikoPatientDetail.secondaryInactive.insuranceId.current != nikoPatientDetail.secondaryInactive.insuranceId.initial || nikoPatientDetail.secondaryInactive.policyNo.current != nikoPatientDetail.secondaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.eligibilityStatusId != (nikoPatientDetail.initialNikoPatientDetail ? nikoPatientDetail.initialNikoPatientDetail.EligibilityStatusId : "")) ||
                nikoPatientDetail.patientTemplates.some(pt => !pt.isNew && pt.isPriorAuthNeeded != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded))
            );
    }

    @action.bound
    createInsuranceCompareModel(nikoPatientDetail) {
        nikoPatientDetail.primaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.primaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.secondaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.secondaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };

        const fill = (list, targetKey) => {
            if (!list) return;
            const map = targetKey === 'current' ?
                { type: 'type', status: 'status', id: 'id', insuranceId: 'insuranceId', insurance: 'insurance', policyNo: 'policyNo', startDate: 'startDate', endDate: 'endDate' }
                :
                { type: 'Type', status: 'Status', id: 'Id', insuranceId: 'InsuranceId', insurance: 'Insurance', policyNo: 'PolicyNo', startDate: 'StartDate', endDate: 'EndDate' };

            list.forEach(i => {
                const key = `${i[map.type].toLowerCase()}${i[map.status]}`;
                const target = nikoPatientDetail[key];
                if (!target) return;
                target.id[targetKey] = i[map.id];
                target.insuranceId[targetKey] = i[map.insuranceId];
                target.insurance[targetKey] = i[map.insurance];
                target.policyNo[targetKey] = i[map.policyNo];
                target.startDate[targetKey] = i[map.startDate];
                target.endDate[targetKey] = i[map.endDate];
            });
        };

        fill(nikoPatientDetail.nikoPatientDetailInsurances, 'current');
        if (nikoPatientDetail.initialNikoPatientDetail)
            fill(nikoPatientDetail.initialNikoPatientDetail.NikoPatientDetailInsurances, 'initial');
    }

    @action.bound
    getPatientStatusButtonClassName(nikoPatientDetail, status = null) {
        nikoPatientDetail.hospitalClassName = "";
        nikoPatientDetail.dischargedClassName = "";
        nikoPatientDetail.expiredClassName = "";
        nikoPatientDetail.activeClassName = "";

        this.statuses.filter(i => !status || i.id === status).forEach((item) => {
            if (this.selectedStatusAbrv === "in-progress") {
                if (item.id === "Hospital" && nikoPatientDetail.status === 'Hospital') {
                    nikoPatientDetail.hospitalClassName = "is-active--alt"
                }
                else if (item.id === "Active" && nikoPatientDetail.status !== 'Hospital') {
                    nikoPatientDetail.activeClassName = "is-active";
                }
            }
            else if (this.selectedStatusAbrv === "discharge") {
                if (item.id === "Hospital" && nikoPatientDetail.status === 'Hospital') {
                    nikoPatientDetail.hospitalClassName = "is-active";
                }
                else if (item.id === "Discharged" && nikoPatientDetail.status === 'Discharged') {
                    nikoPatientDetail.dischargedClassName = "is-active";
                }
                else if (item.id === "Expired" && nikoPatientDetail.status === 'Expired') {
                    nikoPatientDetail.expiredClassName = "is-active";
                }
                else if (item.id === "Active" && nikoPatientDetail.status !== 'Hospital' && nikoPatientDetail.status !== 'Discharged' && nikoPatientDetail.status !== 'Expired') {
                    nikoPatientDetail.activeClassName = "is-active";
                }
            }
            else if (this.selectedStatusAbrv === "intake-reviewed" && item.id === "Active") {
                nikoPatientDetail.activeClassName = "is-active";
            }
        });
    }

    @action.bound
    async goToFacilityPreview() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
    }

    @action.bound
    async goToFacilityCalendar() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderingTypeId}`);
    }

    @action.bound
    orderHistory() {
        window.open(this.rootStore.routerStore.findRoute("master.app.main.niko-facility-order.history").pattern.replace(":id", this.facilityOrder.id));
    }

    @action.bound
    async startInitialIntakeStatus() {
        await this.setInitialIntakeStatusStarted();
    }

    @action.bound
    async setInitialIntakeStatusStarted() {
        if (this.initialIntakeStatus) {
            return;
        }

        this.disabled = true;
        try {
            const status = this.lookups.importHeaderStatus.find(i => i.abrv === 'initial-intake');
            await this.facilityOrderStatusService.create({ facilityOrderId: this.facilityOrder.id, statusId: status.id, startedOn: new Date(), startedByUserId: this.rootStore.userStore.user.id });
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            this.disabled = false;
        }
    }

    @action.bound
    async setInitialIntakeStatusComplete() {
        if (!this.initialIntakeStatus || this.initialIntakeStatus.completedOn) {
            return;
        }

        this.disabled = true;
        try {
            const request = { ...this.initialIntakeStatus, completedOn: new Date(), completedByUserId: this.rootStore.userStore.user.id };
            await this.facilityOrderStatusService.update(request);
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            this.disabled = false;
        }
    }

    @action.bound
    async restartInitialIntakeReview() {
        this.disabled = true;
        try {
            const request = { ...this.initialIntakeStatus, completedOn: null, completedByUserId: null };
            await this.facilityOrderStatusService.update(request);
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            this.disabled = false;
        }
    }

    @action.bound
    async getFacilityOrderStatus() {
        var statuses = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
        runInAction(() => {
            this.facilityOrder.facilityOrderStatuses.replace(statuses.data.item);
        });
        this.refreshKey = uuid();
    }

    @action.bound
    async checkIsReviewed(setStatus = true) {
        let params = {
            facilityOrderId: this.id,
            intakeStatusIds: `${this.activeNewFlowStatusIds},${this.dischargeStatusId}`,
            isInitialIntakeReviewed: false,
            pageSize: 1,
            fields: 'id',
        }
        this.isReviewed = !(await this.patientTemplateService.find(params)).data.item.some(x => x.id);

        if (setStatus) {
            this.shouldAutoNavigateToDischargesTab();
        }
    }

    @action.bound
    shouldAutoNavigateToDischargesTab() {
        if (this.selectedStatusAbrv == "in-progress" && !this.isReviewed && this.initialIntakeStatus && !this.initialIntakeStatus.completedOn && this.nikoPatientDetails.length == 0) {
            this.setStatus('discharge');
        }
    }

    @action.bound
    filterByCategory(selected) {
        this.itemCategories = selected.value;
        this.queryUtility.fetch();
    }

    isMasterPatient = (nikoPatientDetail) => {
        return this.facilityOrder && this.facilityOrder.facility
            && this.facilityOrder.facility.nikoMasterPatientId
            && this.facilityOrder.facility.nikoMasterPatientId == nikoPatientDetail.nikoPatientId;
    }

    isCheckingDeliveryLocation = false;

    @action.bound
    async openCreateNewPatientModal() {
        if (this.isCheckingDeliveryLocation) {
            return;
        }
        const facilityId = this.facilityOrder ? this.facilityOrder.facilityId : null;
        if (facilityId) {
            this.isCheckingDeliveryLocation = true;
            let loadingToastId = null;
            const loadingToastTimeout = setTimeout(() => {
                loadingToastId = this.rootStore.notificationStore.warning("Checking Facility Delivery Location in Niko...", false);
            }, 1000);
            let hasDeliveryLocation = true;
            try {
                const facilityService = new FacilityService(this.rootStore.application.baasic.apiClient);
                const response = await facilityService.hasNikoDeliveryLocation(facilityId);
                hasDeliveryLocation = !(response && response.data && !response.data.hasDeliveryLocation);
            } finally {
                clearTimeout(loadingToastTimeout);
                if (loadingToastId !== null) {
                    this.rootStore.notificationStore.dismiss(loadingToastId);
                }
                this.isCheckingDeliveryLocation = false;
            }
            if (!hasDeliveryLocation) {
                return this.rootStore.notificationStore.error("Action not allowed: this Facility has no Delivery Location in Niko. Add the Delivery Location in Niko and try again.");
            }
        }
        this.nikoCreateNewPatientModal.open({
            facilityOrderId: this.id,
            facilityOrder: this.facilityOrder,
            facilityId: facilityId,
            fetchPatientTemplates: async () => { this.queryUtility.fetch(); },
            pageName: this.pageName,
            pageType: 'II',
        });
    }

    @action.bound
    async openNikoTemplateCreateModal(nikoPatientDetail) {
        const isMasterPatient = this.isMasterPatient(nikoPatientDetail);

        if (isMasterPatient) {
            await this.nikoFacilityOrderNewFlowExtraSuppliesAddModal.open({
                facilityOrderId: this.facilityOrder.id,
                sendAgoraPo: this.facilityOrder.facility.sendAgoraPo,
                skipAutoItems: true,
                fetchData: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id)
            });
        } else {
            await this.nikoTemplateCreateModal.open({
                nikoPatientDetail: nikoPatientDetail,
                fetchPatientTemplates: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id),
                pageName: this.pageName,
                tabName: this.selectedStatusAbrv
            });
        }
    }

    @action.bound
    async openNikoPatientStatusModal(nikoPatientDetail, status) {
        if (nikoPatientDetail.status === status && !(nikoPatientDetail.hospitalClassName == "is-active--alt" && status == "Hospital")) {
            return;
        }
        this.nikoPatientStatusModal.open({
            status: status,
            nikoPatientDetail: nikoPatientDetail,
            pageName: this.pageName,
            tabName: this.selectedStatusAbrv,
            additionalMessage: this.selectedStatusAbrv == "discharge" && status == 'Active' && 'Patient will be reviewed upon activation. Please confirm that you\'ve applied all necessary updates to the patient\'s templates.',
            skipValidation: true,
            getNikoPatient: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id)
        });
    }

    @action.bound
    async reviewPatientTemplates(nikoPatientDetail, patientTemplateId, isReview) {
        nikoPatientDetail.disabled = true;
        try {
            const updatedItems = _.map(nikoPatientDetail.patientTemplates.filter((pt) => pt.id && (!patientTemplateId || pt.id == patientTemplateId)), (pt) => {
                return {
                    id: pt.id,
                    isReviewed: isReview,
                    pageName: this.pageName,
                };
            });
            await this.patientTemplateService.reviewPatientTemplate(updatedItems);
            this.fetchPatientTemplateByPatient(nikoPatientDetail.id);
        }
        catch (err) {
            this.rootStore.notificationStore.error("An unexpected error occurred while trying to review the template.");
        }
        finally {
            nikoPatientDetail.disabled = false;
        }
    }

    @action.bound
    async reviewAllPatientTemplates() {
        this.disabled = true;
        try {
            const patientTemplates = this.nikoPatientDetails.reduce((result, nikoPatientDetail) => {
                result = result.concat(nikoPatientDetail.patientTemplates.filter(obj => obj !== undefined && !obj.isInitialIntakeReviewed));
                return result;
            }, []);

            if (patientTemplates && patientTemplates.length > 0) {
                const updatedItems = _.map(patientTemplates, (pt) => {
                    return {
                        id: pt.id,
                        isReviewed: true,
                        pageName: this.pageName,
                    };
                });
                await this.patientTemplateService.reviewPatientTemplate(updatedItems);
            }

        } catch (err) {
            this.rootStore.notificationStore.error("An unexpected error occurred while trying to update the template.", err);
        }
        finally {
            this.queryUtility.fetch();
            this.disabled = false;
        }
    }

    @action.bound
    changeItemsVisibility(patientTemplate) {
        patientTemplate.itemsVisible = !patientTemplate.itemsVisible;
    }

    @action.bound
    showPreviousFlag(nikoPatientDetail) {
        return nikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail && (nikoPatientDetail.status != nikoPatientDetail.initialNikoPatientDetail.Status);
    }

    @action.bound
    showTemplateChangeWarning(
        nikoPatientDetail,
        eligibilityStatusChange,
        nonEligibilityStatusChange,
        isParUpdated,
        template
    ) {
        if (!(nikoPatientDetail && nikoPatientDetail.status == "Active"))
            return;

        if (isParUpdated) {
            return this.templateChangeConfirmModal.open({ message: "PAR flag was updated on a clinically reviewed template. Please ensure that the change you made is accounted for." });
        }

        if (!(nikoPatientDetail.eligibilityStatus && nikoPatientDetail.eligibilityStatus.name == "Eligible"))
            return;

        if (template && template.isClinicalReviewed && !template.isDiscontinued) {
            return this.templateChangeConfirmModal.open({ message: "This template has already been reviewed by Clinical Review. Please ensure that the change you made is accounted for." });
        }
        else if (eligibilityStatusChange && nikoPatientDetail && nikoPatientDetail.patientTemplates.find(x => x.isClinicalReviewed && !x.isDiscontinued)) {
            return this.templateChangeConfirmModal.open({ message: "At least one of this patient's templates has already been reviewed by Clinical Review. Please ensure that the change you made is accounted for." });
        }
    }

    @action.bound
    async openPatientTemplateMissingProductNoteModal(patientTemplate) {
        this.nikoTemplateMissingProductNoteModal.open({
            patientTemplate: patientTemplate,
            showTemplateChangeWarning: () => this.showTemplateChangeWarning(this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId), false, false, false, patientTemplate)
        });
    }

    @action.bound
    async openPatientTemplateNoteModal(patientTemplate) {
        this.patientTemplateNoteModal.open({
            patientTemplate: patientTemplate
        });
    }

    @action.bound
    async openPatientTemplateDiscontinuedModal(patientTemplate) {
        const nikoPatientDetail = this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId);

        this.patientTemplateDiscontinuedModal.open({
            icon: "cross-circle",
            title: `${patientTemplate.isDiscontinued ? "Unmark" : "Mark"} As Discontinued`,
            additionalMessage: patientTemplate.isDiscontinued && nikoPatientDetail.status === 'Active'
                ? "Template will be reviewed upon activation. Please confirm that you've applied all necessary updates to the template."
                : undefined,
            action: () => this.markDiscontinued(patientTemplate),
        });
    }

    @action.bound
    async markDiscontinued(patientTemplate) {
        // ISG-871: the backend runs drainage bag / leg bag validations when activating an Active patient's template and returns warnings.
        const response = await this.patientTemplateService.updateDiscontinuedStatus({ id: patientTemplate.id, isDiscontinued: !patientTemplate.isDiscontinued, pageName: this.pageName });
        await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);

        const validationMessages = response && response.data && response.data.messages;
        if (validationMessages && validationMessages.length) {
            validationMessages.forEach((message) => this.rootStore.notificationStore.warning(message));
        }
    }

    @action.bound
    async openPatientTemplateDeleteModal(nikoPatientDetail, patientTemplate) {
        let params = { facilityOrderId: this.id, nikoPatientDetailId: patientTemplate.nikoPatientDetailId, pageSize: 3, fields: 'Id,TemplateId' };
        var response = (await this.patientTemplateService.find(params)).data.item;
        this.patientTemplateDeleteModal.open({
            item: patientTemplate,
            deleteTemplate: () => this.deleteTemplate(patientTemplate),
            numberOfTemplates: response.every(x => x.id == patientTemplate.id || x.templateId == patientTemplate.id) ? 1 : 2,
            isNewPatient: nikoPatientDetail.isNew
        });
    }

    @action.bound
    async deleteTemplate(patientTemplate) {
        patientTemplate.disabled = true;
        try {
            await this.patientTemplateService.deleteNewTemplate(patientTemplate.id);
            await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to delete a template.");
        }
        finally {
            patientTemplate.disabled = false;
        }
    }

    @action.bound
    async updatePatientTemplateNote(patientTemplate) {
        const newNote = patientTemplate.tempNote ? patientTemplate.tempNote.trim() : null;
        patientTemplate.isAddingNote = false;
        if (patientTemplate.note !== newNote) {
            patientTemplate.note = newNote;
            patientTemplate.tempNote = newNote;
            await this.patientTemplateService.update(patientTemplate);
        }
    }

    @action.bound
    addNoteToTemplate(patientTemplate) {
        patientTemplate.isAddingNote = true;
        patientTemplate.tempNote = "";
    }

    @action.bound
    async addEmptyItem(patientTemplate) {
        if (patientTemplate.patientTemplateItems.some(i => !i.productId)) {
            return;
        }

        await this.setProductStore(patientTemplate);
        patientTemplate.patientTemplateItems.push({
            id: uuid(),
            quantity: 0,
            salesOrderQuantity: null,
            note: undefined,
            itemCals: undefined,
            itemMls: undefined,
            mlph: undefined,
            frequency: undefined,
            procedureCode: null,
            patientTemplatId: patientTemplate.id,
            productId: null,
            product: null,
            actionId: this.insertImportActionId,
            toggle: !this.templateChangeConfirmModal.isOpen
        });
    }

    @action.bound
    async setProductStore(patientTemplate) {
        const nikoPatientDetail = this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId);
        const isMasterPatient = nikoPatientDetail && this.isMasterPatient(nikoPatientDetail);

        patientTemplate.productStore = new BaasicDropdownStore({
            filterable: true,
            textField: "itemId",
        }, {
            fetchFunc: async (term) => {
                if ((term && term.trim()) === '')
                    term = undefined;

                let params = {
                    searchQuery: term,
                    rpp: 50,
                    subCategoryId: patientTemplate.subCategoryId,
                    isActive: true,
                    embed: "Product,Product.ItemType,Product.ItemCategory,Product.SubCategory,Product.UnitOfMeasure,Product.ProcedureCode_"
                }

                if (patientTemplate.itemCategoryId === this.ostomyItemCategoryId) {
                    params = { ...params, sort: "isPreferred|desc" };
                }

                const response = await this.itemSubcategoryService.find(params);
                let filteredProducts = isMasterPatient ? response.data.item : response.data.item.filter(x => !x.product.itemId.toLowerCase().includes("-cs"));
                return _.map(filteredProducts, item => {
                    return {
                        ...item.product
                    };
                });
            }
        });
    }

    @action.bound
    async saveItemOnProductSelect(patientTemplate, patientTemplateItem, product) {
        patientTemplate.disabled = true;
        if (!patientTemplate.isNew && patientTemplate.subCategory.abrv === 'Trach-Tube' && patientTemplate.patientTemplateItems.some(x => x.productId && x.actionId !== this.deleteImportActionId)) {
            this.rootStore.modalStore.showConfirm('FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_DELTE_TRACH_TUBE_ITEMS', async () => {
                await this.saveItem(patientTemplate, patientTemplateItem, product);
            });
        }
        else {
            await this.saveItem(patientTemplate, patientTemplateItem, product);
        }
    }

    @action.bound
    async saveItem(patientTemplate, patientTemplateItem, product) {
        try {
            const nikoPatientDetail = this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId);
            const isMasterPatient = nikoPatientDetail && this.isMasterPatient(nikoPatientDetail);

            const isEnteral = patientTemplate.itemCategoryId === this.enteralItemCategoryId;
            const isFormula = product.itemTypeId || (product.procedureCode_ && product.procedureCode_.abrv === "B4155");

            if (isMasterPatient) {
                const response = await this.patientTemplateItemService.create({
                    product: product,
                    productId: product.id,
                    patientTemplateId: patientTemplate.id,
                    quantity: 0,
                    salesOrderQuantity: 0,
                    note: null,
                    procedureCode: product.procedureCode_ && product.procedureCode_.abrv,
                });
                const created = { ...response.data, product: product };
                const pos = patientTemplate.patientTemplateItems.findIndex((i) => patientTemplateItem.id === i.id);
                if (pos > -1) {
                    patientTemplate.patientTemplateItems.splice(pos, 1, created);
                    await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
                }
                else {
                    patientTemplate.patientTemplateItems.push(created);
                }
                this.showTemplateChangeWarning(nikoPatientDetail, false, false, false, patientTemplate);
                return;
            }

            let tempItem = {
                product: product,
                productId: product.id,
                procedureCode: product.procedureCode_ && product.procedureCode_.abrv,
            };

            if (isEnteral && isFormula) {
                _.extend(tempItem, { itemMls: 1, itemCals: product.calsPerMl });
            }

            const items = (await this.patientTemplateItemService.createAdditionalItems(
                {
                    patientTemplateItem: tempItem,
                    patientTemplateItems: patientTemplate.patientTemplateItems.filter((i) => i.productId).map((i) => { return { id: i.id, product: i.product, productId: i.productId, procedureCode: i.procedureCode, actionId: i.actionId }; }),
                    facilityId: this.facilityOrder.facilityId,
                    subCategoryId: patientTemplate.subCategoryId,
                    clinicalStatusId: patientTemplate.clinicalStatusId,
                    patientTemplateId: patientTemplate.id,
                    pageName: this.pageName,
                    tabName: this.selectedStatusAbrv,
                }
            )).data;

            for (const item of items) {
                let updatepatientTemplateItem = patientTemplate.patientTemplateItems.find(x => x.id === item.id);
                if (updatepatientTemplateItem) {
                    updatepatientTemplateItem.procedureCode = item.procedureCode;
                    continue;
                }

                const response = await this.patientTemplateItemService.create({
                    id: item.id,
                    product: item.product,
                    productId: item.product.id,
                    patientTemplateId: patientTemplate.id,
                    quantity: item.quantity || 0,
                    salesOrderQuantity: item.salesOrderQuantity || 0,
                    note: item.note || null,
                    procedureCode: item.procedureCode,
                });
                const created = { ...response.data, product: item.product };
                const pos = patientTemplate.patientTemplateItems.findIndex((i) => patientTemplateItem.id === i.id);
                if (pos > -1) {
                    patientTemplate.patientTemplateItems.splice(pos, 1, created);
                    await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
                }
                else {
                    patientTemplate.patientTemplateItems.push(created);
                }
            }
            this.showTemplateChangeWarning(this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId), false, false, false, patientTemplate);

            if (isEnteral && isFormula) {
                const formulaItem = patientTemplate.patientTemplateItems.find(i => i.product && i.product.id === tempItem.productId);
                if (formulaItem) {
                    this.openNikoPatientTemplateItemCalculationModal(formulaItem, patientTemplate, false);
                }
            }

        }
        catch (err) {
            this.rootStore.notificationStore.error(err.data ? err.data : "Error while adding additional items");
        }
        finally {
            patientTemplate.disabled = false;
        }
    }

    @action.bound
    openNikoPatientTemplateItemCalculationModal(patientTemplateItem, patientTemplate, showWarning = true) {
        this.nikoPatientTemplateItemCalculationModal.open({
            patientTemplateItem: patientTemplateItem,
            facilityId: this.facilityOrder.facilityId,
            patientTemplateItemService: this.patientTemplateItemService,
            updateItem: async (patientTemplateItem) => {
                await this.updateItem(patientTemplateItem);
                await this.fetchPatientTemplateItemsById(patientTemplate.nikoPatientDetailId, patientTemplate.id);
                await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
            },
            showTemplateChangeWarning: showWarning ? () => this.showTemplateChangeWarning(this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId), false, false, false, patientTemplate) : undefined,
        });
    }

    @action.bound
    editItem(item, propertyName, value) {
        const initialItem = item.initialPatientTemplateItem;
        const fieldMap = { quantity: 'Quantity', salesOrderQuantity: 'SalesOrderQuantity', note: 'Note' };
        const confirmedMap = { quantity: 'isQuantityConfirmed', salesOrderQuantity: 'isSalesOrderQuantityConfirmed', note: 'isNoteConfirmed' };

        if (initialItem && fieldMap[propertyName] && initialItem[fieldMap[propertyName]] == item[propertyName]) {
            set(item, confirmedMap[propertyName], false);
        }

        if (propertyName === "quantity" && initialItem && initialItem.SalesOrderQuantity == item.salesOrderQuantity) {
            set(item, 'isSalesOrderQuantityConfirmed', false);
        }

        item[propertyName] = value;
        if (propertyName === "quantity") {
            item["salesOrderQuantity"] = value;
        }
    }

    @action.bound
    async updateItem(patientTemplateItem, patientTemplate) {
        if (patientTemplate) {
            patientTemplate.disabled = true;
        }

        if (Number.isNaN(patientTemplateItem.quantity) || patientTemplateItem.quantity === '' || patientTemplateItem.quantity === null || patientTemplateItem.quantity === undefined) {
            patientTemplateItem.quantity = 0;
        }
        if (Number.isNaN(patientTemplateItem.salesOrderQuantity) || patientTemplateItem.salesOrderQuantity === '' || patientTemplateItem.salesOrderQuantity === null || patientTemplateItem.salesOrderQuantity === undefined) {
            patientTemplateItem.salesOrderQuantity = 0;
        }

        if (patientTemplateItem.quantity == patientTemplateItem.savedQuantity &&
            patientTemplateItem.salesOrderQuantity == patientTemplateItem.savedSalesOrderQuantity &&
            patientTemplateItem.note == patientTemplateItem.savedNote) {
            if (patientTemplate) {
                patientTemplate.disabled = false;
            }
            return;
        }

        await this.patientTemplateItemService.update(patientTemplateItem.id, {
            productId: patientTemplateItem.productId,
            patientTemplateId: patientTemplateItem.patientTemplateId,
            quantity: patientTemplateItem.quantity,
            salesOrderQuantity: patientTemplateItem.salesOrderQuantity,
            note: patientTemplateItem.note,
            procedureCode: patientTemplateItem.procedureCode,
            mlph: patientTemplateItem.mlph,
            itemMls: patientTemplateItem.itemMls,
            itemCals: patientTemplateItem.itemCals,
            frequency: patientTemplateItem.frequency,
            actionId: patientTemplateItem.actionId
        });

        patientTemplateItem.savedQuantity = patientTemplateItem.quantity;
        patientTemplateItem.savedSalesOrderQuantity = patientTemplateItem.salesOrderQuantity;
        patientTemplateItem.savedNote = patientTemplateItem.note;
        set(patientTemplateItem, 'isQuantityConfirmed', true);
        set(patientTemplateItem, 'isSalesOrderQuantityConfirmed', true);
        set(patientTemplateItem, 'isNoteConfirmed', true);
        if (patientTemplate) {
            await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
            this.showTemplateChangeWarning(this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId), false, false, false, patientTemplate);
            patientTemplate.disabled = false;
        }
    }

    @action.bound
    async deletePatientTemplateItem(patientTemplateItem, patientTemplate, nikoPatientDetail) {
        patientTemplate.disabled = true;
        if (nikoPatientDetail.isNew && nikoPatientDetail.patientTemplates.every(x => x.id == patientTemplate.id || x.templateId == patientTemplate.id) && patientTemplate.patientTemplateItems.length == 1) {
            let params = { facilityOrderId: this.id, nikoPatientDetailId: patientTemplate.nikoPatientDetailId, pageSize: 3, fields: 'Id,TemplateId' };
            var response = (await this.patientTemplateService.find(params)).data.item;

            if (response.every(x => x.id == patientTemplate.id || x.templateId == patientTemplate.id)) {
                return this.nikoPatientTemplateItemNewFlowDeleteQuestionModal.open({
                    deleteItem: () => this.deleteItem(patientTemplate, patientTemplateItem, false),
                    deletePatient: () => this.deleteItem(patientTemplate, patientTemplateItem, true)
                });
            }
        }

        if (patientTemplateItem.actionId == this.insertImportActionId || patientTemplate.isNew) {
            await this.deleteItem(patientTemplate, patientTemplateItem, patientTemplate.isNew);
        }
        else {
            await this.setAction(patientTemplateItem, this.deleteImportActionId, patientTemplate);
        }
        patientTemplate.disabled = false;
    }

    @action.bound
    async deleteItem(patientTemplate, patientTemplateItem, deleteTemplate) {
        patientTemplate.disabled = true;
        if (deleteTemplate && patientTemplate.patientTemplateItems.length === 1) {
            return await this.deleteTemplate(patientTemplate);
        }

        if (patientTemplateItem.productId) {
            const procedureCode = patientTemplateItem.product && patientTemplateItem.product.procedureCode_ && patientTemplateItem.product.procedureCode_.abrv;
            const hasSyringeItem = patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some(x => x.product && x.product.defaultItemTypeId == this.syringeDefaultItemTypeId);
            await this.patientTemplateItemService.delete(patientTemplateItem.id, { setProcedureCode: (procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem });
            runInAction(() => {
                patientTemplate.patientTemplateItems = patientTemplate.patientTemplateItems.filter((i) => i !== patientTemplateItem);
            });

            if ((procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem) {
                await this.fetchPatientTemplateItemsById(patientTemplate.nikoPatientDetailId, patientTemplate.id);
            }
            await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
            this.showTemplateChangeWarning(this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId), false, false, false, patientTemplate);
        }
        else {
            runInAction(() => {
                patientTemplate.patientTemplateItems = patientTemplate.patientTemplateItems.filter((i) => i !== patientTemplateItem);
            });
        }

        patientTemplate.disabled = false;
    }

    @action.bound
    async swapPatientTemplateItem(patientTemplateItem, patientTemplate, nikoPatientDetail) {
        patientTemplate.disabled = true;
        try {
            if (patientTemplateItem.actionId === this.deleteImportActionId) {
                await this.setAction(patientTemplateItem, this.updateImportActionId, patientTemplate)
            }
            else {
                await this.deletePatientTemplateItem(patientTemplateItem, patientTemplate, nikoPatientDetail)
            }
            this.addEmptyItem(patientTemplate);
        }
        catch (err) {
            this.rootStore.notificationStore.error("FACILITY_ORDER_TEMPLATE_ITEM.EDIT.DELETE_ERROR");
        }
        patientTemplate.disabled = false;
    }

    @action.bound
    async setAction(patientTemplateItem, actionId, patientTemplate) {
        patientTemplateItem.actionId = actionId;
        const procedureCode = patientTemplateItem.product && patientTemplateItem.product.procedureCode_ && patientTemplateItem.product.procedureCode_.abrv;
        const hasSyringeItem = patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some(x => x.product && x.product.defaultItemTypeId == this.syringeDefaultItemTypeId);
        await this.patientTemplateItemService.update(patientTemplateItem.id, { ...patientTemplateItem, setProcedureCode: (procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem });
        if ((procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem) {
            await this.fetchPatientTemplateItemsById(patientTemplate.nikoPatientDetailId, patientTemplate.id);
        }
        await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
        this.showTemplateChangeWarning(this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId), false, false, false, patientTemplate);
    }

    @action.bound
    async getFieldHistory(item, fieldName, value) {
        if (item.history) {
            let history;
            if (fieldName === 'ImportAction') {
                history = (await this.historyService.getItemActionHistory(item.id)).data;
            } else {
                history = (await this.historyService.getRecordHistory({ recordId: item.id, fieldName: fieldName, value: value })).data;
            }
            item.history.userName = history.userName;
            item.history.eventDate = history.eventDate;
            item.history.fieldName = fieldName;
            if (history.action) item.history.action = history.action;
        }
    }

    @action.bound
    async openNikoHealthPatient(remoteId) {
        window.open(`${this.rootStore.applicationStore.app.baasic.settings.nikoHealthBaseUrl}v2/#/patients/${remoteId}/demographics`);
    }
}

export default NikoFacilityOrderInitialIntakePreviewViewStore;
